/**
 * Server-backed reads for the admin panel, with a small Firestore-shaped API.
 *
 * These screens previously queried Firestore directly using the operator's
 * browser privileges. Routing reads through authenticated Express endpoints
 * lets the server enforce access centrally and prepares us for scoped staff
 * roles without rewriting every table, filter, and detail view.
 *
 * Query chains only describe a request; get() sends it to /api/admin/records.
 * The server authorizes each request and allowlists resources, filters, and
 * ordering. Returned records expose id/exists/data()/get() and reconstruct
 * timestamps and pagination cursors so existing renderers keep working.
 * Queries without a limit follow all server pages to preserve existing views.
 *
 * This is a read-only compatibility layer, not a general Firestore client.
 * Mutations use explicit action endpoints. New resource access must be added
 * to the server policy; client checks are never an authorization boundary.
 * Moving traffic here does not itself revoke existing Firestore rule grants.
 */
import Auth from "../Auth";
import { decodeAdminValue } from "../../../src/shared/adminRecordCodec";

// Match the subset of DocumentSnapshot used by the existing admin renderers.
// Decode once so repeated data()/get() calls share the same hydrated timestamps.
export function adminRecord(dto) {
  const data = decodeAdminValue(dto.value);

  return {
    id: dto.id,
    exists: dto.exists,
    data: () => data,
    get: field => field.split(".").reduce((value, key) => value?.[key], data),
  };
}

function querySnapshot(docs) {
  return {
    docs,
    empty: docs.length === 0,
    size: docs.length,
    forEach: callback => docs.forEach(callback),
  };
}

// Query builders are immutable: a screen can reuse a base query for different
// filters/pages without changing a request it has already constructed.
class AdminQuery {
  constructor(resource, query = {}) {
    this.resource = resource;
    this.query = query;
  }

  where(field, operator, value) {
    const serializedValue = value instanceof Date ? value.toISOString() : value;
    const filters = [...(this.query.filters || []), [field, operator, serializedValue]];

    return new AdminQuery(this.resource, { ...this.query, filters });
  }

  orderBy(field, direction) {
    return new AdminQuery(this.resource, {
      ...this.query,
      order: [field, direction],
    });
  }

  limit(limit) {
    return new AdminQuery(this.resource, { ...this.query, limit });
  }

  startAfter(record) {
    if (!record) return this;

    // The server sorts by document ID after createdAt to break timestamp ties.
    // Send the original values so pagination still works if the record changes
    // or disappears. Converting to Date would lose nanosecond precision.
    const after = { id: record.id };
    if (this.query.order) {
      const timestamp = record.get("createdAt");
      after.createdAt = {
        seconds: timestamp.seconds,
        nanoseconds: timestamp.nanoseconds,
      };
    }

    return new AdminQuery(this.resource, { ...this.query, after });
  }

  doc(id) {
    // A document lookup is independent of the query's filters and pagination.
    return {
      get: async () => {
        const result = await Auth.fetchWithIdToken(
          `/api/admin/records/${this.resource}/get`,
          { id },
        );
        return adminRecord(result.record);
      },
    };
  }

  async get() {
    const docs = [];
    let query = this.query;

    while (true) {
      const result = await Auth.fetchWithIdToken(
        `/api/admin/records/${this.resource}/query`,
        { query },
      );
      docs.push(...result.records.map(adminRecord));

      // An explicit limit means the screen manages its own pagination. Without
      // one, preserve the old "load all" behavior by following server cursors.
      // Each server request is still bounded and independently authorized.
      if (this.query.limit || !result.next) break;
      query = { ...query, after: result.next };
    }

    return querySnapshot(docs);
  }
}

export const adminRecords = {
  collection: resource => new AdminQuery(resource),
};

// Project detail views also need document metadata stored under the owner's
// userData record. Express derives that owner from the project; the browser
// supplies only the project ID. Keep the shape expected by the Project model.
export async function readAdminProject(id) {
  const [record, metadata] = await Promise.all([
    adminRecords.collection("projects").doc(id).get(),
    Auth.fetchWithIdToken(`/api/admin/projects/${encodeURIComponent(id)}/metadata`),
  ]);

  if (!record.exists) return null;

  const metadataEntries = metadata.records.map(dto => {
    const doc = adminRecord(dto);
    return [doc.id, { id: doc.id, ...doc.data() }];
  });

  return {
    id,
    data: record.data(),
    metaData: Object.fromEntries(metadataEntries),
  };
}
